import { z } from "zod";
import { paidAttributionSchema } from "./paid-attribution";

/** Advertising consent is separate from an artist's mailing-list consent. */
export const adContextSchema = z.object({
  consent: z.literal(true),
  receiptId: z.string().uuid(),
  browserId: z.string().uuid(),
  sourceUrl: z.string().url().max(450),
  userAgent: z.string().max(450),
  fbp: z.string().max(200).optional(),
  fbc: z.string().max(450).optional(),
  ttp: z.string().max(200).optional(),
  ttclid: z.string().max(450).optional(),
  utm: paidAttributionSchema.optional(),
});
export type AdContext = z.infer<typeof adContextSchema>;
export type AdEvent = {
  name:
    | "PageView"
    | "ViewContent"
    | "InitiateCheckout"
    | "Purchase"
    | "FreeUnlock"
    | "AlreadyUnlocked";
  id: string;
  time: number;
  sourceUrl: string;
  artistId: string;
  accessId?: string;
  contentType?: "event" | "access";
  source?: "guest_sign_in";
  value?: number;
  currency?: string;
};

/** Each Stripe metadata value stays below its 500-character limit. */
export function adContextMetadata(
  context?: AdContext | null,
): Record<string, string> {
  if (!context) return {};
  const { utm, ...fields } = context;
  return Object.fromEntries(
    Object.entries({ ...fields, ...utm }).map(([key, value]) => [
      `ads_${key}`,
      String(value),
    ]),
  );
}
export function adContextFromMetadata(
  metadata: Record<string, unknown>,
): AdContext | null {
  const input = Object.fromEntries(
    Object.entries(metadata)
      .filter(([key]) => key.startsWith("ads_"))
      .map(([key, value]) => [
        key.slice(4),
        key === "ads_consent" ? value === "true" : value,
      ]),
  );
  const utm = paidAttributionSchema.safeParse(input);
  if (utm.success) input.utm = utm.data;
  const parsed = adContextSchema.safeParse(input);
  return parsed.success ? parsed.data : null;
}

export function adEventProperties(event: AdEvent) {
  return {
    artist_id: event.artistId,
    ...(event.source ? { source: event.source } : {}),
    ...(event.accessId
      ? {
          content_ids: [event.accessId],
          content_id: event.accessId,
          // Vendors reserve content_type for product/product_group. Preserve the
          // domain distinction in content_category on every step of the funnel.
          content_type: "product",
          content_category: event.contentType,
        }
      : {}),
    ...(event.value !== undefined && event.currency
      ? { value: event.value, currency: event.currency }
      : {}),
  };
}
